<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isVisible" class="modal-backdrop" @click="handleBackdropClick">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h2>Miniblock Details</h2>
            <button class="close-button" @click="close"><XMarkIcon class="close-icon" /></button>
          </div>
          <div class="modal-body" v-if="miniblock">
            <div class="detail-row">
              <span class="detail-label">Block Number:</span>
              <span class="detail-value">#{{ miniblock.blockNumber.toLocaleString() }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Miniblock Index:</span>
              <span class="detail-value">{{ miniblock.index }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Transactions:</span>
              <span class="detail-value">{{ miniblock.txCount.toLocaleString() }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Gas Used:</span>
              <span class="detail-value">{{ formatGas(miniblock.gasUsed) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Timestamp:</span>
              <span class="detail-value">{{ formatTimestamp(miniblock.timestamp) }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { XMarkIcon } from '@/components/icons';
import type { MiniblockData } from '@/rendering/BarGraphRenderer';

const isVisible = ref(false);
const miniblock = ref<MiniblockData | null>(null);

const open = (data: MiniblockData) => {
  miniblock.value = data;
  isVisible.value = true;
};

const close = () => {
  isVisible.value = false;
};

const handleBackdropClick = () => {
  close();
};

const formatGas = (gas: string): string => {
  const gasNum = parseInt(gas);
  if (gasNum >= 1_000_000) {
    return `${(gasNum / 1_000_000).toFixed(2)}M`;
  } else if (gasNum >= 1_000) {
    return `${(gasNum / 1_000).toFixed(2)}K`;
  }
  return gas;
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

// Expose methods for parent component
defineExpose({
  open,
  close
});
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--modal-overlay);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0;
  min-width: 500px;
  max-width: 600px;
  box-shadow: var(--shadow-lg);
  animation: modal-appear 0.2s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border);
  position: relative;
  background: var(--bg-elevated);
  border-radius: 8px 8px 0 0;
}

.modal-header h2 {
  font-family: Inter, Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--accent-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.close-button {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: 1px solid var(--error);
  color: var(--error);
  font-size: 24px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  font-family: Inter, Arial, sans-serif;
  line-height: 1;
}

.close-button:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.close-icon {
  width: 18px;
  height: 18px;
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--hover-overlay);
  border-radius: 4px;
  font-family: Inter, Arial, sans-serif;
  transition: all 0.2s;
}

.detail-row:hover {
  background: var(--active-overlay);
  border-left: 3px solid var(--accent-primary);
}

.detail-label {
  color: var(--text-secondary);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

.detail-value {
  color: var(--accent-primary);
  font-size: 14px;
  font-weight: 700;
}

/* Transition animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.2s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}
</style>
